import * as React from "react";
import { useNavigate } from "react-router";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { CursorResult, JourneyWithRelations } from "@volley/data";

import { fetchApi } from "../../../util";
import { inProgressUrl } from "../../Journeys/journeyNav";
import { type Sport } from "../../common/context/sport";
import useJourney from "../../hooks/useJourney";

export async function shouldShowCutterChallenge(
    selectedSport: Sport,
    controller: AbortController,
): Promise<JourneyWithRelations | null> {
    if (selectedSport !== "PLATFORM_TENNIS") {
        return null;
    }

    const url = new URL("/api/journeys", window.location.origin);
    url.searchParams.set("limit", "100");
    url.searchParams.set("offset", "0");
    url.searchParams.set("type", "assessment");
    url.searchParams.set("sport", selectedSport);

    try {
        const result = await fetchApi<CursorResult<JourneyWithRelations>>(
            url.toString(),
            "GET",
            undefined,
            {
                signal: controller.signal,
            },
        );
        const candidate = result.result.find(
            (j) => j.name === "CUTTER_CHALLENGE",
        );

        return candidate ?? null;
    } catch {
        return null;
    }
}

interface CutterChallengeCardProps {
    journey: JourneyWithRelations;
}

export default function CutterChallengeCard({
    journey,
}: CutterChallengeCardProps): React.JSX.Element | null {
    const navigate = useNavigate();
    const { startJourney, registerJourneyLinkIntent } = useJourney();

    const [dialogOpen, setDialogOpen] = React.useState(false);

    const onConfirm = React.useCallback(async () => {
        if (!journey) return;

        const instance = await startJourney(journey.id);
        const firstWorkout = journey.journeyAppWorkouts.at(0);
        if (firstWorkout) {
            await registerJourneyLinkIntent(
                instance.ulid,
                firstWorkout.appWorkout.id,
            );
            navigate(
                `/content/apps/workouts/plugin/play/${firstWorkout.appWorkout.appId}/${firstWorkout.appWorkout.id}`,
            );
        } else {
            navigate(
                inProgressUrl({
                    journeyType: journey.type,
                    journeyInstanceId: instance.id,
                    journeyInstanceUlid: instance.ulid,
                }),
            );
        }
    }, [journey, startJourney, registerJourneyLinkIntent, navigate]);

    return (
        <>
            <Box
                onClick={() => setDialogOpen(true)}
                sx={{
                    cursor: "pointer",
                    borderRadius: 2,
                    overflow: "hidden",
                    background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.info.main} 100%)`,
                    minHeight: 95,
                    width: "100%",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                    "&:hover": {
                        "& .chevron": {
                            transform: "translateX(4px)",
                        },
                    },
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                >
                    <Stack spacing={0.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <EmojiEventsIcon
                                sx={{
                                    color: "secondary.main",
                                    fontSize: 20,
                                }}
                            />
                            <Typography
                                variant="h4"
                                sx={{
                                    color: "#fff",
                                    fontWeight: "bold",
                                }}
                            >
                                {journey.title ?? "Cutter Challenge"}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "rgba(255,255,255,0.7)",
                                fontSize: "0.75rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                            }}
                        >
                            {journey.description ?? "Join the challenge!"}
                        </Typography>
                    </Stack>

                    <ChevronRightIcon
                        className="chevron"
                        sx={{
                            color: "#fff",
                            fontSize: 32,
                            opacity: 0.7,
                            transition: "transform 0.2s ease",
                            flexShrink: 0,
                        }}
                    />
                </Stack>
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogContent>
                    <DialogContentText sx={{ pb: 2 }}>
                        Are you ready to start{" "}
                        <strong>{journey?.title ?? ""}</strong>?
                    </DialogContentText>
                    <DialogContentText>
                        {journey?.description ?? ""}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="inherit"
                        autoFocus
                        onClick={() => setDialogOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button color="inherit" onClick={onConfirm}>
                        Let&apos;s go!
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
